import { create } from "zustand"
import { useRef, FC, useMemo, useEffect } from 'react'
import { Modal } from 'bootstrap'
import { Formik, Form, FormikValues, FormikProps } from "formik"
import * as Yup from 'yup'

import { cloneDeep } from 'lodash'
import { FormikMultiFileUpload, FormikSwitch, FormikTextField, StatusErrorMessage } from "../../../../../../components/widgets"
import { SubmitButtonRow } from "../../../../../../components/layout"
import { IWebFileAttachment } from "../../../../applications"
import { isPopulatedArray } from "../../../../../../components/helpers"

interface IDLMWithdrawalDialogModel  {    
  note: string
  noteAttachments: Array<IWebFileAttachment>           
  hasNote?: 'yes' | 'required'
  hasAttachment?: 'yes' | 'required'
  sendBilling: boolean
}

const initDLMWithdrawalDialogValues = {
  note: '',
  noteAttachments: [],  
  hasNote: undefined,
  hasAttachment: undefined,
  sendBilling: false,
}

const DLMWithdrawalDialogSchema: any = Yup.object({
  hasNote: Yup.string().trim(),
  hasAttachment: Yup.string().trim(),
  note: Yup.string().trim().label('Note')
  .when('hasNote', {
      is: (val: string) => (val === 'required'),
      then: (schema) => schema.required(),
  }),        
  noteAttachments: Yup.array()
  .when('hasAttachment', {
      is: (val: string) => (val === 'required'),
      then: (schema) => schema.min(1, 'Attachment required').required(),
  }),          
  sendBilling: Yup.boolean(),           
});


const DLMWithrawalDialogForm: FC<FormikProps<IDLMWithdrawalDialogModel>> = ({ values, status }) => {
  
  const { toggleShow, cancelText, submitText} = useDLMWithrawalDialogState(state => state);

  return(
  <Form id='kt_doclabel_withdrawal_dialog_form' className='form' noValidate>
    <div className='d-flex flex-column p-4' >
      { (values?.hasNote !== undefined) &&
      <div className="row mb-4">        
        <FormikTextField size="12" label="Note" name="note" rows={3} required={values.hasNote === 'required'} />
      </div>
      }
      { (values?.hasAttachment !== undefined) &&
      <div className="row mb-4">        
        <FormikMultiFileUpload size="12" label="Note Attachment/s" name="noteAttachments" required={values.hasAttachment === 'required'}/>
      </div>    
      }
      <div className="row mb-4">
        <FormikSwitch size="12" label="Send to Billing" name="sendBilling" />
      </div>
        
      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={() => toggleShow(false)} cancelText={cancelText} submitText={submitText} />
    </div>
  </Form>
  )
}


type DLMWithrawalModalProps = {
  title: string
  message: string
  onOK: ((values?:any) => void) | undefined
  noteValue?: string  
  onCancel?: Function
  hasNote?: 'yes' | 'required'
  hasAttachment?:  'yes' | 'required'
  submitText?: string
  cancelText?: string
}

interface DLMWithrawalDialogState {
  title: string
  message: string
  noteValue?: string
  onOK: ((values?:any) => void) | undefined
  onCancel: Function | undefined
  hasNote?: 'yes' | 'required'
  hasAttachment?:  'yes' | 'required'
  submitText?: string
  cancelText?: string
  show: boolean,
  toggleShow: (show: boolean) => void
  initModal: (params: DLMWithrawalModalProps) => void
}

export const useDLMWithrawalDialogState = create<DLMWithrawalDialogState>()((set, get) => ({
  title: '',
  message: '',
  noteValue: '',
  onOK: undefined,
  onCancel: undefined,
  hasNote: undefined,
  hasAttachment:  undefined,
  submitText: 'YES',
  cancelText: 'NO',
  show: false, 
  toggleShow: (show: boolean) => { if(get().show !== show) set({show});},
  initModal: (params: DLMWithrawalModalProps) => set({
    noteValue: '',
    onCancel: undefined,
    hasNote: undefined,
    hasAttachment:  undefined,
    submitText: 'YES',
    cancelText: 'NO',
    ...params
  }),
  clear: () => set({ 
    title: '',
    message: '',
    noteValue: '',
    onOK: undefined,
    onCancel: undefined,
    hasNote: undefined,
    hasAttachment:  undefined,
    submitText: 'YES',
    cancelText: 'NO',
    show: false, 
  }),
}));


export const DLMWithrawalDialog =  () => {
        
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { show, hasNote, hasAttachment, noteValue, onOK, onCancel, message, title, toggleShow} = useDLMWithrawalDialogState(state => state);

  const initValues = useMemo(() => {
    if (show === false) {
      return cloneDeep(initDLMWithdrawalDialogValues);
    }
    let base = {
      ...cloneDeep(initDLMWithdrawalDialogValues),
      hasNote,
      hasAttachment,
    }

    if (noteValue) {
      Object.assign(base, { note: noteValue })
    }

    return(base);        
  }, [show, hasNote, hasAttachment, noteValue]);
    
  const doSubmit = (values: any, actions: FormikValues) => {        
    const { setStatus, setSubmitting } = actions;
    setStatus('');    
    if (onOK) {
      let payload = {};
      
      if (hasNote && values.note) Object.assign(payload, { note : values.note});
      if (hasAttachment && isPopulatedArray(values?.noteAttachments))  Object.assign(payload, { noteAttachments : values.noteAttachments.map( (o: IWebFileAttachment) => o.id)});
      if (values?.sendBilling) Object.assign(payload, { sendBilling : true});
      
      onOK(payload);          
    }
    setSubmitting(false);
    toggleShow(false);
  }

  useEffect(() => {
    if (! (modalRef && modalRef.current)) return;
  
    const modalEle = modalRef.current;    
    if (show) {
      const bsModal = new Modal(modalEle, {
        backdrop: false,
        keyboard: false
      })
      bsModal.show();
      return;                
    } 
    const bsModal= Modal.getInstance(modalEle)
    if (bsModal) bsModal.hide();       
    if (onCancel) onCancel();              
  // eslint-disable-next-line          
  }, [show, modalRef]);

  return (
  <div className="modal" ref={modalRef} id='appDLMWithrawalDialog' data-bs-backdrop="static" data-bs-keyboard="false" aria-labelledby="modalDLMWithdrawalDialogBackdropLabel" aria-hidden="true">
    <div className="modal-dialog">
      <div className="modal-content">
        <div className="modal-header">
          <h1 className="modal-title fs-5" id="modalDLMWithdrawalDialogBackdropLabel">{title}</h1>
          <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => toggleShow(false)}></button>
        </div>
        <div className="modal-body">
          <div className="text-center">
            {message}
          </div>                    
          <Formik
            initialValues={initValues}
            validationSchema={DLMWithdrawalDialogSchema}
            enableReinitialize={true}
            onSubmit={doSubmit}
            component={DLMWithrawalDialogForm}
          />
        </div>                
      </div>
    </div>
  </div>
  )
};